// MARK: Imports
import { TextInput } from "@mantine/core";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useDebouncedState } from "@mantine/hooks";

// Components
import { CustomTable, Filters, PageHeader, TableSkeleton } from "@components/";

// Requests
import { useDeleteClassification, useGetAllClassifications } from "@api/";

// Constants
import {
  CLASSIFICATIONS_TABLE_HEADER,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_TIME,
  DEFAULT_PER_PAGE,
  DEFAULT_TOTAL,
  DEFAULT_TOTAL_PAGES,
} from "@constants/";

// Enums
import { EEntity, EFilters, EPagination } from "@enums/";

const AllClassifications = () => {
  // MARK: State
  const [search, setSearch] = useDebouncedState("", DEFAULT_DEBOUNCE_TIME);
  const [paginator, setPaginator] = useState({
    [EPagination.PER_PAGE]: DEFAULT_PER_PAGE,
    [EPagination.CURRENT_PAGE]: DEFAULT_CURRENT_PAGE,
    [EPagination.TOTAL]: DEFAULT_TOTAL,
    [EPagination.TOTAL_PAGES]: DEFAULT_TOTAL_PAGES,
  });

  const searchValue = useMemo(() => {
    return search;
  }, [search]);

  // MARK: Functions
  const { deleteClassification } = useDeleteClassification();
  const { classifications, isLoading } = useGetAllClassifications({
    [EFilters.SEARCH]: searchValue,
    [EFilters.PAGE]: paginator[EPagination.CURRENT_PAGE],
    [EFilters.PER_PAGE]: paginator[EPagination.PER_PAGE],
  });

  const onPageChange = (newPage: number) => {
    setPaginator((prev) => {
      return { ...prev, [EPagination.CURRENT_PAGE]: newPage };
    });
  };

  const resetFilters = useCallback(() => {
    setSearch("");
  }, [setSearch]);

  // MARK: Effects
  // Set initial pagination from backend
  useEffect(() => {
    if (!classifications || classifications?.length === 0) return;
    setPaginator(classifications?.pagination);
  }, [classifications]);

  // MARK: Renders
  if (isLoading) return <TableSkeleton />;

  return (
    <section>
      <PageHeader showCreateBtn title="Classifications" entity={EEntity.CLASSIFICATION} />

      <Filters onReset={resetFilters}>
        <form>
          <TextInput
            defaultValue={search}
            onChange={(event) => setSearch(event?.currentTarget?.value)}
            placeholder="Search by classification name..."
            variant="unstyled"
          />
        </form>
      </Filters>

      <div className="w-full">
        <CustomTable
          columns={CLASSIFICATIONS_TABLE_HEADER}
          entity={EEntity.CLASSIFICATION}
          handleDelete={deleteClassification}
          onPageChange={onPageChange}
          paginator={classifications.pagination}
          showDelete
          showEdit
          tableData={classifications.data}
        />
      </div>
    </section>
  );
};

export { AllClassifications };
